import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Divider, Grid, Card, CardContent, Container, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    IconButton, Tooltip, Snackbar, Alert, CircularProgress, useTheme, InputAdornment, useMediaQuery, Stack, Paper
} from '@mui/material';
import {
    Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon, Search as SearchIcon,
    ArrowUpward, ArrowDownward, UnfoldMore, Edit as EditIcon
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import api from '../../../services/api';
import Paginate from '../../../components/Paginate';
import UserFormDialog from './UserFormDialog';

const itemsPerPage = 6;

const formatUserType = (type) => {
    switch (type) {
        case 'admin':
            return 'Administrador';
        case 'team':
            return 'Equipe Técnica';
        case 'city_hall':
            return 'Prefeitura';
        default:
            return type;
    }
};

const UsersPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [users, setUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [page, setPage] = useState(1);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [userToDeleteId, setUserToDeleteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/users');
            setUsers(response.data);
        } catch (error) {
            showSnackbar('Erro ao carregar usuários', 'error');
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const removeAccents = (str) => {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    };

    const visibleUsers = useMemo(() => {
        const normalizeStringForSearch = (str) => {
            return str
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
                .replace(/\s+/g, '');
        };

        const normalizedSearchTerm = normalizeStringForSearch(searchTerm);

        let filteredUsers = users.filter(user =>
            normalizeStringForSearch(user.name).includes(normalizedSearchTerm) ||
            user.NumCreated.toString().includes(searchTerm)
        );

        if (sortConfig.key) {
            filteredUsers.sort((a, b) => {
                const aValue = normalizeStringForSearch(a[sortConfig.key] || '');
                const bValue = normalizeStringForSearch(b[sortConfig.key] || '');

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredUsers.slice(startIndex, endIndex);
    }, [users, page, searchTerm, sortConfig]);

    const filteredUsersCount = useMemo(() => {
        const normalizedSearchTerm = removeAccents(searchTerm.toLowerCase());
        return users.filter(user =>
            removeAccents(user.name.toLowerCase()).includes(normalizedSearchTerm) ||
            removeAccents(user.email.toLowerCase()).includes(normalizedSearchTerm) ||
            user.NumCreated.toString().includes(searchTerm)
        ).length;
    }, [users, searchTerm]);

    const handleOpenDialog = (user = null) => {
        setCurrentUser(user);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentUser(null);
    };

    const handleDelete = (id) => {
        setUserToDeleteId(id);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/users/${userToDeleteId}`);
            showSnackbar('Usuário excluído com sucesso.', 'success');
            const response = await api.get('/users');
            const updatedUsers = response.data;
            setUsers(updatedUsers);

            const filteredUsers = updatedUsers.filter(user =>
                removeAccents(user.name.toLowerCase()).includes(removeAccents(searchTerm.toLowerCase())) ||
                removeAccents(user.email.toLowerCase()).includes(removeAccents(searchTerm.toLowerCase())) ||
                user.NumCreated.toString().includes(searchTerm)
            );
            const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

            if (page > totalPages && totalPages > 0) {
                setPage(page - 1);
            } else if (totalPages === 0) {
                setPage(1);
            }
        } catch (error) {
            showSnackbar('Erro ao excluir o usuário.', 'error');
            console.error('Error deleting user:', error);
        } finally {
            setOpenConfirmDialog(false);
            setUserToDeleteId(null);
        }
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setUserToDeleteId(null);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth={isMobile ? 'xs' : 'xl'} sx={{ mt: isMobile ? 1 : 0, mb: 4, px: isMobile ? 1 : 2 }}>
                <Grid container spacing={isMobile ? 1 : 3} sx={{ justifyContent: 'center', mt: 0 }}>
                    <Grid item xs={12} sm={10} md={8}>
                        <Card sx={{
                            borderRadius: isMobile ? '8px' : '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            p: isMobile ? 1 : 2,
                            mt: 0
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: isMobile ? 0 : 0
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'space-between',
                                    alignItems: isMobile ? 'center' : 'center',
                                    mb: isMobile ? 2 : 1,
                                    width: '100%',
                                    gap: isMobile ? 2 : 0
                                }}>
                                    <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                                        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#10641c', fontWeight: '600' }}>
                                            Usuários
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#666', mt: 0.5 }}>
                                            Total: <strong>{filteredUsersCount}</strong>
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenDialog(null)}
                                        sx={{
                                            bgcolor: '#10641c',
                                            '&:hover': { bgcolor: '#0a3d0f' },
                                            fontSize: isMobile ? '0.9rem' : '0.875rem',
                                            px: isMobile ? 2 : 3
                                        }}
                                    >
                                        Novo Usuário
                                    </Button>
                                </Box>

                                <Divider sx={{ mb: isMobile ? 2 : 2, width: '100%' }} />
                                <Box sx={{ width: '100%', mb: isMobile ? 2 : 2 }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Buscar usuário..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        sx={{
                                            height: '45px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: isMobile ? '6px' : '8px',
                                                fontSize: isMobile ? '1rem' : '1rem',
                                                height: 'inherit',
                                                padding: '0 14px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#ced4da'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#10641c'
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#10641c'
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: isMobile ? '1rem' : '1rem'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize={isMobile ? 'medium' : 'medium'} />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>

                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
                                        <CircularProgress size={isMobile ? 30 : 40} />
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%' }}>
                                        {isMobile ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {visibleUsers.length === 0 ? (
                                                    <Typography sx={{ fontSize: '1rem', color: '#666', textAlign: 'center' }}>
                                                        Nenhum usuário encontrado
                                                    </Typography>
                                                ) : (
                                                    visibleUsers.map((user) => (
                                                        <Card key={user.id} sx={{
                                                            borderRadius: '8px',
                                                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                                                            p: 2
                                                        }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                        Nome:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '0.9rem' }}>
                                                                        {user.name}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                        Email:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '0.9rem' }}>
                                                                        {user.email}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                        Tipo:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '0.9rem' }}>
                                                                        {formatUserType(user.type)}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                        Qtd. de Usuários Cadastrados:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '0.9rem' }}>
                                                                        {user.NumCreated}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                                                                    <Tooltip title="Editar">
                                                                        <IconButton
                                                                            onClick={() => handleOpenDialog(user)}
                                                                            sx={{ color: '#1976d2', p: 1 }}
                                                                        >
                                                                            <EditIcon fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Excluir">
                                                                        <IconButton
                                                                            onClick={() => handleDelete(user.id)}
                                                                            sx={{ color: '#d32f2f', p: 1 }}
                                                                        >
                                                                            <DeleteIcon fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </Box>
                                                        </Card>
                                                    ))
                                                )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                                <TableContainer component={Paper} sx={{ maxWidth: '100%' }}>
                                                    <Table sx={{
                                                        minWidth: 850,
                                                        '& .MuiTableCell-head': { fontWeight: 'bold' },
                                                        '& .MuiTableCell-root': {
                                                            fontSize: '0.875rem',
                                                            p: 1
                                                        }
                                                    }} aria-label="users table">
                                                        <TableHead sx={{ bgcolor: '#f8f8f8' }}>
                                                            <TableRow>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '30%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('name')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Nome
                                                                        {sortConfig.key === 'name' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '30%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('email')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Email
                                                                        {sortConfig.key === 'email' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '20%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('type')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Tipo
                                                                        {sortConfig.key === 'type' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '20%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('NumCreated')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Qtd. de Usuários Cadastrados
                                                                        {sortConfig.key === 'NumCreated' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ width: '20%' }}>
                                                                    Ações
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {visibleUsers.length === 0 ? (
                                                                <TableRow>
                                                                    <TableCell align="center" colSpan={5}>
                                                                        <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                                                                            Nenhum usuário encontrado.
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                visibleUsers.map((user) => (
                                                                    <TableRow
                                                                        key={user.id}
                                                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}
                                                                    >
                                                                        <TableCell align="center">{user.name}</TableCell>
                                                                        <TableCell align="center">{user.email}</TableCell>
                                                                        <TableCell align="center">{formatUserType(user.type)}</TableCell>
                                                                        <TableCell align="center">{user.NumCreated}</TableCell>
                                                                        <TableCell align="center">
                                                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                                                <Tooltip title="Editar">
                                                                                    <IconButton
                                                                                        onClick={() => handleOpenDialog(user)}
                                                                                        sx={{ color: '#1976d2', p: 1 }}
                                                                                    >
                                                                                        <EditIcon fontSize="medium" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Excluir">
                                                                                    <IconButton
                                                                                        onClick={() => handleDelete(user.id)}
                                                                                        sx={{ color: '#d32f2f', p: 1 }}
                                                                                    >
                                                                                        <DeleteIcon fontSize="medium" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </Stack>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {filteredUsersCount > itemsPerPage && (
                    <Box sx={{ mt: isMobile ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
                        <Paginate
                            count={Math.ceil(filteredUsersCount / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                            size={isMobile ? 'small' : 'medium'}
                        />
                    </Box>
                )}

                <UserFormDialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    currentUser={currentUser}
                    fetchUsers={fetchUsers}
                    showSnackbar={showSnackbar}
                    isMobile={isMobile}
                />

                <Dialog
                    open={openConfirmDialog}
                    onClose={handleCloseConfirmDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'sm'}
                    fullScreen={isMobile}
                >
                    <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        Confirmar Exclusão
                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 1 : 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: isMobile ? '1rem' : '1rem' }}>
                            Tem certeza que deseja excluir este usuário? <br /> Esta ação é irreversível.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseConfirmDialog}
                            sx={{
                                bgcolor: '#10641c',
                                color: 'white',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            variant="contained"
                            color="error"
                            sx={{
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<DeleteIcon />}
                        >
                            Excluir
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%', fontSize: isMobile ? '0.9rem' : '0.875rem' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default UsersPage;